import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import {
  executiveGovernanceStandardPath,
  fetchExecutiveStandardsSummary,
  type ExecutiveStandardMiniCard,
} from '../../services/executiveGovernanceApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import styles from '../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss'
import EnterpriseOperationalSection, {
  EnterpriseOperationalCard,
} from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  standardCodes: string[]
  enterpriseUx?: boolean
}

function levelColor(level?: string): string {
  switch (level) {
    case 'CRITICAL':
    case 'AT_RISK':
      return '#ef4444'
    case 'DEGRADED':
    case 'DECLINING':
      return '#f59e0b'
    case 'WATCH':
    case 'STABLE':
    case 'IMPROVING':
      return '#10b981'
    default:
      return '#64748b'
  }
}

function MiniCard({
  card,
  onOpen,
  enterpriseUx,
}: {
  card: ExecutiveStandardMiniCard
  onOpen: (code: string) => void
  enterpriseUx?: boolean
}) {
  const { t } = useI18n()
  const code = card.standardCode
  const name = getStandardDisplayName(t, code)

  if (!card.hasEnoughHistory) {
    const body = (
      <>
        <p className={enterpriseUx ? entStyles.postureSubtitle : styles.executiveMiniEmpty}>
          {t('executiveGovernance.notEnoughExecutiveHistory', 'Not enough executive history yet')}
        </p>
        <Button type="button" size="small" color="outline" onClick={() => onOpen(code)}>
          {t('executiveGovernance.openExecutiveDrilldown', 'Open executive drill-down')}
        </Button>
      </>
    )
    if (enterpriseUx) {
      return <EnterpriseOperationalCard title={name}>{body}</EnterpriseOperationalCard>
    }
    return (
      <article className={styles.executiveMiniCard}>
        <h4 className={styles.executiveMiniTitle}>{name}</h4>
        {body}
      </article>
    )
  }

  const metricRows = [
    {
      label: t('executiveGovernance.certificationStability', 'Certification Stability'),
      value: card.certificationRiskLevel || '—',
      color: levelColor(card.certificationRiskLevel),
    },
    {
      label: t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory'),
      value: card.readinessTrajectory || '—',
      color: levelColor(card.readinessTrajectory),
    },
    {
      label: t('executiveGovernance.operationalPressure', 'Operational Pressure'),
      value: String(card.operationalPressure ?? 0),
    },
    {
      label: t('executiveGovernance.degradedControls', 'degraded controls'),
      value: String(card.degradedControlCount ?? 0),
    },
    {
      label: t('executiveGovernance.blockers', 'Blockers'),
      value: String(card.blockerCount ?? 0),
    },
    ...(card.staleEvidenceCount != null
      ? [
          {
            label: t('executiveGovernance.staleEvidence', 'Stale evidence'),
            value: String(card.staleEvidenceCount),
          },
        ]
      : []),
  ]

  const metrics = enterpriseUx ? (
    <dl className={entStyles.metricRows}>
      {metricRows.map((row) => (
        <div key={row.label} className={entStyles.metricRow}>
          <dt>{row.label}</dt>
          <dd style={row.color ? { color: row.color } : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  ) : (
    <dl className={styles.executiveMiniMetrics}>
      {metricRows.map((row) => (
        <div key={row.label} className={styles.executiveMiniRow}>
          <dt>{row.label}</dt>
          <dd style={row.color ? { color: row.color } : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  )

  if (enterpriseUx) {
    return (
      <EnterpriseOperationalCard title={name}>
        {metrics}
        <div className={entStyles.cardActions}>
          <Button type="button" size="small" color="outline" onClick={() => onOpen(code)}>
            {t('executiveGovernance.openExecutiveDrilldown', 'Open executive drill-down')}
          </Button>
        </div>
      </EnterpriseOperationalCard>
    )
  }

  return (
    <article className={styles.executiveMiniCard}>
      <h4 className={styles.executiveMiniTitle}>{name}</h4>
      {metrics}
      <Button type="button" size="small" color="outline" onClick={() => onOpen(code)}>
        {t('executiveGovernance.openExecutiveDrilldown', 'Open executive drill-down')}
      </Button>
    </article>
  )
}

export default function ExecutiveStandardsOverviewBlock({ standardCodes, enterpriseUx }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cards, setCards] = useState<ExecutiveStandardMiniCard[]>([])

  const codesKey = standardCodes.join(',')

  const load = useCallback(async () => {
    if (!standardCodes.length) {
      setCards([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchExecutiveStandardsSummary(standardCodes)
      setCards(data.cards || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load executive standards summary')
      setCards([])
    } finally {
      setLoading(false)
    }
  }, [codesKey])

  useEffect(() => {
    void load()
  }, [load])

  const onOpen = (code: string) => {
    navigate(executiveGovernanceStandardPath(code))
  }

  if (!standardCodes.length) return null

  const grid = (
    <>
      {loading ? (
        <p className={enterpriseUx ? entStyles.postureSubtitle : styles.signalsUpdating} aria-live="polite">
          {t('common.loading', 'Loading…')}
        </p>
      ) : null}
      {error ? (
        <p className={enterpriseUx ? entStyles.registryError : styles.registryError} role="alert">
          {error}
        </p>
      ) : null}
      {!loading && !error && cards.length === 0 ? (
        <p className={enterpriseUx ? entStyles.postureSubtitle : styles.executiveMiniEmpty}>
          {t('executiveGovernance.notEnoughExecutiveHistory', 'Not enough executive history yet')}
        </p>
      ) : null}
      {!loading && cards.length > 0 ? (
        <div className={enterpriseUx ? entStyles.standardGrid : styles.executiveMiniGrid}>
          {cards.map((card) => (
            <MiniCard key={card.standardCode} card={card} onOpen={onOpen} enterpriseUx={enterpriseUx} />
          ))}
        </div>
      ) : null}
    </>
  )

  if (enterpriseUx) {
    return (
      <EnterpriseOperationalSection
        layerLabel={t('executiveGovernance.layerLabel', 'Executive governance')}
        title={t('executiveGovernance.executiveStatusByStandard', 'Executive status by standard')}
        subtitle={t(
          'executiveGovernance.executiveStatusByStandardHint',
          'Compact executive posture per enabled standard — open drill-down for full detail.',
        )}
        testId="cc-overview-executive-enterprise"
      >
        {grid}
      </EnterpriseOperationalSection>
    )
  }

  return (
    <section className={styles.signalsSection} aria-labelledby="executive-standards-overview-title">
      <h3 id="executive-standards-overview-title" className={styles.signalsTitle}>
        {t('executiveGovernance.executiveStatusByStandard', 'Executive status by standard')}
      </h3>
      <p className={styles.signalsSubtitle}>
        {t(
          'executiveGovernance.executiveStatusByStandardHint',
          'Compact executive posture per enabled standard — open drill-down for full detail.',
        )}
      </p>
      {grid}
    </section>
  )
}
